import React from 'react';
import { Box, Typography, Collapse } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { colors } from '../../theme/colors';

interface CollapsibleSectionProps {
  title: string;
  icon?: React.ReactNode;
  expanded: boolean;
  onToggle: (expanded: boolean) => void;
  accentColor?: string;
  children: React.ReactNode;
  rightContent?: React.ReactNode;
}

/**
 * Reusable collapsible section with consistent styling.
 * - Collapsed: Just title row with chevron (no background)
 * - Expanded: Left accent border with content
 */
export const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  icon,
  expanded,
  onToggle,
  accentColor = colors.blue[400],
  children,
  rightContent,
}) => {
  return (
    <Box>
      {/* Header row - always visible */}
      <Box
        onClick={() => onToggle(!expanded)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          py: 1,
          px: 2,
          cursor: 'pointer',
          borderRadius: 1,
          '&:hover': { bgcolor: colors.gray[50] },
        }}
      >
        {/* Expand/collapse chevron */}
        {expanded ? (
          <ExpandMoreIcon sx={{ fontSize: 18, color: colors.gray[500] }} />
        ) : (
          <ChevronRightIcon sx={{ fontSize: 18, color: colors.gray[400] }} />
        )}

        {/* Optional icon */}
        {icon && (
          <Box sx={{ display: 'flex', alignItems: 'center', color: accentColor }}>
            {icon}
          </Box>
        )}

        {/* Title */}
        <Typography
          variant="body2"
          sx={{
            color: expanded ? colors.gray[700] : colors.gray[500],
            fontWeight: 500,
          }}
        >
          {title}
        </Typography>

        {/* Flex spacer */}
        <Box sx={{ flex: 1 }} />

        {/* Optional right content (e.g., count badge) */}
        {rightContent}
      </Box>

      {/* Collapsible content */}
      <Collapse in={expanded}>
        <Box
          sx={{
            borderLeft: `3px solid ${accentColor}`,
            ml: 2,
            pl: 2,
            py: 1,
          }}
        >
          {children}
        </Box>
      </Collapse>
    </Box>
  );
};

export default CollapsibleSection;
